/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 *
 * Shared form component for creating and editing sources
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { concat, difference, filter, find, intersectionWith, isEmpty, sortBy, values } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { Button, Col, Form, FormControl, Row } from "react-bootstrap";

import Alert from "react-bootstrap/Alert";
import Container from "../../common/components/layout/Container";
import Panel from "../../common/components/layout/Panel";
import FormContainer from "../../common/components/layout/FormContainer";
import Image from "react-bootstrap/Image";
import FormLabel from "../../common/components/form/FormLabel";
import styled from "styled-components";

import BreadCrumb from "../../common/components/BreadCrumb";
import PanelHeader from "../../common/components/layout/PanelHeader";
import FormSelect from "../../common/components/form/FormSelect";
import FormTextInput from "../../common/components/FormTextInput";
import FormTextArea from "../../common/components/form/FormTextArea";
import FormRadioGroup from "../../common/components/form/FormRadioGroup";
import CheckBox from "../../common/components/CheckBox";
import SpinnerOverlay from "../../common/components/SpinnerOverlay";
import FormNumericInput from "../../common/components/FormNumericInput";
import SectionTogglePanel from "../../common/components/SectionTogglePanel";
import DataPreview from "../../common/components/DataPreview";
import HelpPanel from "../../common/components/HelpPanel";
import config from "../../config";

import {
    getInputSchemas,
    updateMappedSelectedFileFields,
    updateAllMappedSelectedFileFields,
    browseS3ForSourceData,
    clearS3Files,
    clearUploadFiles,
    previewDataOnS3ForSourceData,
} from "./sourceSlice";

import { TYPES } from "./SourceTypes";
import { INDICATOR_TYPES, getIndicatorTypeById } from "../../common/metadata/Indicators";
import { STATES } from "../../common/States";

import { useCSVReader } from "react-papaparse";

import BrowserWindowImage from "../../common/assets/img/ToolbeltIcons_Artboard_17.png";
import { FileTreeBrowser } from "../../common/components/FileTreeBrowser";

// Styled Components
const SourceDrop = styled.div`
  display: grid;
  justify-items: center;
  text-align: center;
  width: 100%;
  grid-template-columns: 1fr;

  label {
    margin-bottom: 0;
    font-size: 14px;
    cursor: pointer;
  }
`;

const MappingContainer = styled.div`
  border: 1px dashed #CCC;
  padding: 20px;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100px;
`;

const MappingHeader = styled.div`
  font-weight: bold;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 40px;
  margin-bottom: 15px;
`;

const MappingRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 40px;
  margin-bottom: 15px;
`;

const StyledFileTreeBrowser = styled.div`
  border: dashed 3px #2d5d7e;
  border-radius: 1px;
  height: 100%;
`;

const StyledRow = styled(Row)`
    padding-bottom: 0 !important;
    margin-bottom: 0 !important;
`;

// Note: CSVReader in v4.x doesn't use refs - we track file state via acceptedFile prop

function SourceForm({
    mode,
    initialData,
    sourceData,
    sourcePreview,
    onSave,
    onCancel,
    breadcrumbItems,
    loading,
    alertMessage: externalAlertMessage,
    successMessage: externalSuccessMessage,
}) {
    const dispatch = useDispatch();
    const sourceState = useSelector(state => state.source);
    const s3Files = sourceState.s3Files;
    const { CSVReader } = useCSVReader();

    const productOptions = [
        { value: 'b2b', label: 'b2b' },
        { value: 'tcu', label: 'tcu' }
    ];

    const dataTypes = [
        { value: 'csv', label: 'csv' },
        { value: 'avro', label: 'avro' },
        { value: 'parquet', label: 'parquet' }
    ];

    const dataChannels = [
        { value: 'upload', label: 'Upload' },
        { value: 's3', label: <>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;S3&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</> }
    ];

    const [localAlertMessage, setLocalAlertMessage] = useState('');
    const [dataPickerOrPreviewAlertMessage, setDataPickerOrPreviewAlertMessage] = useState('');
    const [localSuccessMessage, setLocalSuccessMessage] = useState('');

    const [formValid, setFormValid] = useState(true);
    const [type, setType] = useState('');
    const [name, setName] = useState('');
    const [label, setLabel] = useState('');
    const [description, setDescription] = useState('');
    const [url, setUrl] = useState('');
    const [region, setRegion] = useState('');
    const [city, setCity] = useState('');
    const [qualityScore, setQualityScore] = useState('0.8');
    const [isPublic, setIsPublic] = useState(true);
    const [authoritative, setAuthoritative] = useState(false);
    const [products, setProducts] = useState([productOptions[0]]);
    const [indicators, setIndicators] = useState(null);
    const [vendorName, setVendorName] = useState({ value: '', label: '-- Select Vendor --' });
    const [vendorRefs, setVendorRefs] = useState({
        ref1: { key: '', value: '' },
        ref2: { key: '', value: '' },
        ref3: { key: '', value: '' },
        ref4: { key: '', value: '' },
        ref5: { key: '', value: '' }
    });
    const [pipelineRepository, setPipelineRepository] = useState('');
    const [pipelineFileNamePrefix, setPipelineFileNamePrefix] = useState('');
    const [isTestSource, setIsTestSource] = useState(false);

    const [dataType, setDataType] = useState(dataTypes[0]);
    const [dataChannel, setDataChannel] = useState(dataChannels[0].value);
    const [s3BaseKey, setS3BaseKey] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedFileFields, setSelectedFileFields] = useState([]);
    const [selectedFileData, setSelectedFileData] = useState([]);
    const [selectedInputSchema, setSelectedInputSchema] = useState(null);
    const [isNewFileUploaded, setIsNewFileUploaded] = useState(false);
    const mappableInputSchemaProperties = selectedInputSchema && selectedInputSchema.properties.length > 0 ? sortBy(selectedInputSchema.properties, ['ordinalPosition']) : [];
    const mappedSelectedFileFields = sourceState.mappedSelectedFileFields;
    const previewFromS3 = sourceState.previewFromS3;

    const [nameInValid, setNameInValid] = useState(false);
    const [labelInValid, setLabelInValid] = useState(false);
    const [urlInValid, setUrlInValid] = useState(false);
    const [qualityScoreInValid, setQualityScoreInValid] = useState(false);
    const [pipelineFileNamePrefixInValid, setPipelineFileNamePrefixInValid] = useState(false);

    const vendorOptions = [
        { value: '', label: '-- Select Vendor --' },
        { value: 'FGX', label: 'FGX' },
        { value: 'SHOVELS', label: 'Shovels' },
        { value: 'USEARCH', label: 'USearch' },
        { value: 'BRIGHTDATA', label: 'Bright Data' }
    ];

    useEffect(() => {
        setLocalSuccessMessage(externalSuccessMessage?.text || '');
    }, [externalSuccessMessage?.id]);

    useEffect(() => {
        setLocalAlertMessage(externalAlertMessage?.text || '');
    }, [externalAlertMessage?.id]);

    // Populate form with initialData (for edit mode)
    useEffect(() => {
        if (mode === 'edit' && initialData) {
            const source = initialData;

            // Basic fields
            if (source.type) {
                const typeObj = find(values(TYPES), { id: source.type });
                if (typeObj) {
                    // Transform to dropdown format: { value, label, inputType }
                    setType({
                        value: typeObj.id,
                        label: typeObj.display + ' (' + typeObj.inputType + ')',
                        inputType: typeObj.inputType
                    });
                }
            }
            setName(source.name || '');
            setLabel(source.label || '');
            setDescription(source.description || '');
            setUrl(source.url || '');
            setQualityScore(source.qualityScore || '0.8');
            setAuthoritative(source.authoritative || false);
            setIsPublic(source.public !== undefined ? source.public : true);
            setIsTestSource(source.isTest || false);
            if (source.region) {
                const regionObj = find(values(STATES), { id: source.region });
                if (regionObj) {
                    // Transform to dropdown format: { value, label }
                    setRegion({
                        value: regionObj.id,
                        label: regionObj.display
                    });
                }
            }
            setCity(source.city || '');

            // Products
            if (source.products && source.products.length > 0) {
                const selectedProducts = source.products.map(p =>
                    find(productOptions, { value: p })
                ).filter(Boolean);
                setProducts(selectedProducts.length > 0 ? selectedProducts : [productOptions[0]]);
            }

            // Indicators
            if (source.indicators && source.indicators.length > 0) {
                const selectedIndicators = source.indicators.map(i => {
                    const indicator = getIndicatorTypeById(i);
                    return indicator ? { value: indicator.id, label: indicator.display } : null;
                }).filter(Boolean);
                setIndicators(selectedIndicators.length > 0 ? selectedIndicators : null);
            }

            // Vendor
            if (source.vendorConfig && source.vendorConfig.name) {
                const vendor = find(vendorOptions, { value: source.vendorConfig.name });
                setVendorName(vendor || { value: '', label: '-- Select Vendor --' });
            }

            // Vendor references
            if (source.vendorConfig && source.vendorConfig.refs) {
                const refs = { ...vendorRefs };
                Object.keys(source.vendorConfig.refs).forEach((refKey) => {
                    if (refs[refKey] && source.vendorConfig.refs[refKey]) {
                        refs[refKey] = {
                            key: source.vendorConfig.refs[refKey].key || '',
                            value: source.vendorConfig.refs[refKey].value || ''
                        };
                    }
                });
                setVendorRefs(refs);
            }

            // Pipeline
            if (source.pipelineConfig) {
                setPipelineRepository(source.pipelineConfig.repository || '');
                setPipelineFileNamePrefix(source.pipelineConfig.fileNamePrefix || '');
            }
        }
    }, [mode, initialData]);

    // Populate from sourceData in edit mode (following CreateInputContainer pattern)
    // Use ref to track if we've already loaded to avoid infinite loops
    const sourceDataLoadedRef = React.useRef(false);

    useEffect(() => {
        if (mode === 'edit' && sourceData && !sourceDataLoadedRef.current) {
            console.log('Populating from sourceData:', sourceData);
            console.log('Available properties in sourceData:', Object.keys(sourceData));

            // Set DATA TYPE (csv/avro/parquet) and channel (upload/s3)
            // NOTE: This is different from source.type (contractor/supplier/etc)
            if (sourceData.type) {
                const savedDataType = dataTypes.find(dt => dt.value === sourceData.type);
                if (savedDataType) {
                    setDataType(savedDataType);
                }
            }

            if (sourceData.channel) {
                setDataChannel(sourceData.channel);
            }

            // If channel is S3, set up the S3 base key and browse for files
            if (sourceData.channel === 's3' && sourceData.type) {
                let calculatedS3BaseKey = '';
                if (sourceData.type === 'avro') {
                    calculatedS3BaseKey = `${config.sourceInput.s3BaseAvroBucket}${config.sourceInput.s3BaseAvroPath}`;
                } else if (sourceData.type === 'parquet') {
                    calculatedS3BaseKey = `${config.sourceInput.s3BaseParquetBucket}${config.sourceInput.s3BaseParquetPath}`;
                }

                if (calculatedS3BaseKey) {
                    setS3BaseKey(calculatedS3BaseKey);
                    // Dispatch action to populate the file tree
                    dispatch(browseS3ForSourceData({
                        dataType: sourceData.type,
                        s3BaseKey: calculatedS3BaseKey
                    }));
                }
            }

            // Set selectedFile to enable mapping UI (will be overridden by sourcePreview if it has fileName)
            if (sourceData.fileName) {
                setSelectedFile({ name: sourceData.fileName });
            }

            // Populate mappings from sourceData.inputMapping.attributes
            if (sourceData.inputMapping && sourceData.inputMapping.attributes) {
                console.log('Setting field mappings from sourceData.inputMapping.attributes:', sourceData.inputMapping.attributes);

                // Convert field mappings to the format expected by Redux
                // Note: We don't use mappableInputSchemaProperties here to avoid dependency issues
                // The 'required' flag will be false for now - it gets properly set when schema loads
                const existingMappings = Object.entries(sourceData.inputMapping.attributes).map(([property, selectedFileField]) => {
                    return {
                        property,
                        selectedFileField,
                        required: false  // Will be updated by schema loading logic
                    };
                });
                console.log('Dispatching mapped fields:', existingMappings);
                dispatch(updateAllMappedSelectedFileFields(existingMappings));
            } else {
                console.log('No inputMapping.attributes found in sourceData');
            }

            // Mark as loaded to prevent re-running
            sourceDataLoadedRef.current = true;
        }
    }, [mode, sourceData, dispatch]);

    // Populate preview data from API in edit mode
    const sourcePreviewLoadedRef = React.useRef(false);

    useEffect(() => {
        if (mode === 'edit' && sourcePreview && !sourcePreviewLoadedRef.current) {
            console.log('Populating preview data from API:', sourcePreview);

            // Set file name - prefer fileName over key (key is S3 path)
            // Only update if we have an actual fileName (not the S3 path)
            if (sourcePreview.fileName) {
                setSelectedFile({ name: sourcePreview.fileName });
            }
            // Don't set selectedFile if only key exists - sourceData already set the proper fileName

            // Set column headers
            if (sourcePreview.fields && sourcePreview.fields.length > 0) {
                setSelectedFileFields(sourcePreview.fields);
            }

            // Transform records (array of objects) to data (array of arrays)
            if (sourcePreview.records && sourcePreview.records.length > 0) {
                const dataRows = sourcePreview.records.map(record => Object.values(record));
                setSelectedFileData(dataRows);
                console.log(`Loaded ${dataRows.length} preview rows`);
            }

            // Mark as loaded to prevent re-running
            sourcePreviewLoadedRef.current = true;
        }
    }, [mode, sourcePreview]);

    const handleVendorNameChange = (value) => {
        setVendorName(value);
        if (value?.value === 'FGX') {
            setVendorRefs(prev => ({
                ...prev,
                ref1: { ...prev.ref1, key: 'source_id' },
                ref2: { ...prev.ref2, key: 'source_name' },
                ref3: { ...prev.ref3, key: 'url' },
            }));
        }
        else if (value?.value === 'SHOVELS') {
            setVendorRefs(prev => ({
                ...prev,
                ref1: { ...prev.ref1, key: 'parent_source_id' },
                ref2: { ...prev.ref2, key: 'jurisdiction' },
                ref3: { ...prev.ref3, key: 'state' },
            }));
        } else {
            setVendorRefs(prev => ({
                ...prev,
                ref1: { key: '', value: '' },
                ref2: { key: '', value: '' },
                ref3: { key: '', value: '' },
                ref4: { key: '', value: '' },
                ref5: { key: '', value: '' }
            }));
        }
    };

    const isValid = () => {
        if (!type.value) return false;
        if (!name) return false;
        if (!label) return false;
        if (!url) return false;
        if (!qualityScore) { return false; }
        else {
            const _qualityScore = parseFloat(qualityScore);
            if (_qualityScore < 0 || _qualityScore > 1) return false;
        }
        if (nameInValid) return false;
        if (labelInValid) return false;
        if (urlInValid) return false;
        if (qualityScoreInValid) return false;

        // Check that if a vendor reference has a value, it must have a key
        const refs = Object.values(vendorRefs);
        for (const ref of refs) {
            if (ref.value && !ref.key) {
                return false;
            }
        }
        return true;
    };

    const nameValidate = (name) => {
        if (!name) {
            setNameInValid(true); // Blank value IS invalid
            return;
        }
        const nameRegex = /^[A-Za-z0-9_() -]+$/;
        const isValidFormat = nameRegex.test(name);
        setNameInValid(!isValidFormat);
    }

    const qualityScoreValidate = (qualityScore) => {
        if (!qualityScore && qualityScore !== 0) {
            setQualityScoreInValid(true);
            return;
        }
        const numValue = typeof qualityScore === 'string' ? parseFloat(qualityScore) : qualityScore;
        if (isNaN(numValue)) {
            setQualityScoreInValid(true);
            return;
        }
        const isInvalidValue = (numValue < 0 || numValue > 1);
        setQualityScoreInValid(isInvalidValue);
    }

    const labelValidate = (label) => {
        if (!label) {
            setLabelInValid(true); // Blank value IS invalid
            return;
        }
        const labelRegex = /^[A-Za-z0-9_() -]+$/;
        const isValidFormat = labelRegex.test(label);
        setLabelInValid(!isValidFormat);
    }

    const urlValidate = (url) => {
        if (!url) {
            setUrlInValid(true); // Blank value IS invalid (URL is required)
            return;
        }
        if (url === 'NA') {
            setUrlInValid(false);
            return;
        }
        const urlRegex = /^(http:\/\/|https:\/\/)/;
        const isValidFormat = urlRegex.test(url);
        setUrlInValid(!isValidFormat);
    }

    const pipelineFileNamePrefixValidate = (pipelineFileNamePrefix) => {
        if (!pipelineFileNamePrefix) {
            setPipelineFileNamePrefixInValid(false);
            return;
        }
        const isValidFormat = /^[\w-_]+_$/.test(pipelineFileNamePrefix);
        setPipelineFileNamePrefixInValid(!isValidFormat);
    }

    const handleSubmit = async () => {
        if (!isValid()) {
            setFormValid(false);
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth'
            });
        } else {
            setFormValid(true);
            setLocalAlertMessage('');
            setLocalSuccessMessage('');

            const data = {
                source: {
                    type: type.value,
                    name: name.trim(),
                    label: label.trim(),
                    description: description ? description.trim() : null,
                    url: url.trim(),
                    region: region.value ? region.value : null,
                    city: city ? city.trim() : null,
                    qualityScore: !qualityScore ? null : parseFloat(qualityScore),
                    authoritative: authoritative,
                    public: isPublic,
                    isTest: isTestSource,
                    products: products.map((p) => (p.value)),
                    indicators: indicators ? indicators.map((i) => (i.value)) : null,
                    pipelineConfig: {
                        repository: pipelineRepository,
                        fileNamePrefix: pipelineFileNamePrefix
                    },
                    vendorConfig: {
                        name: vendorName?.value || null,
                        refs: {
                            ref1: {
                                key: vendorRefs.ref1.key || null,
                                value: vendorRefs.ref1.value || null
                            },
                            ref2: {
                                key: vendorRefs.ref2.key || null,
                                value: vendorRefs.ref2.value || null
                            },
                            ref3: {
                                key: vendorRefs.ref3.key || null,
                                value: vendorRefs.ref3.value || null
                            },
                            ref4: {
                                key: vendorRefs.ref4.key || null,
                                value: vendorRefs.ref4.value || null
                            },
                            ref5: {
                                key: vendorRefs.ref5.key || null,
                                value: vendorRefs.ref5.value || null
                            }
                        }
                    }
                },
                sourceData: {
                    ...(sourceData || {}),  // Preserve existing sourceData
                    type: dataType.value,
                    channel: dataChannel,
                    fileName: selectedFile?.name || undefined,
                },
                selectedInputSchema: selectedInputSchema,
                selectedFile: selectedFile,
                selectedFileFields: selectedFileFields,
                mappedSelectedFileFields: mappedSelectedFileFields,
                isNewFileUploaded: isNewFileUploaded,
                inputMapping: {
                    attributes: convertMappingsToAttributes()
                }
            };

            onSave(data);
        }
    };

    const convertMappingsToAttributes = () => {
        const attributes = {};
        mappedSelectedFileFields.forEach(m => {
            if (m.property && m.selectedFileField) {
                attributes[m.property] = m.selectedFileField;
            }
        });
        return attributes;
    };

    // Load input schemas on mount
    useEffect(() => {
        dispatch(getInputSchemas());
    }, [dispatch]);

    // Update schema when type changes
    useEffect(() => {
        if (sourceState.inputSchemas && type) {
            setSelectedInputSchema(sourceState.inputSchemas.find((s) => s.inputType === type.inputType));
        }
    }, [sourceState.inputSchemas, type]);

    // Handle S3 preview data
    useEffect(() => {
        // Check that previewFromS3 is an object (not an empty array from initialState)
        if (dataChannel === 's3' && previewFromS3 && typeof previewFromS3 === 'object' && !Array.isArray(previewFromS3)) {
            setSelectedFile({ name: previewFromS3.key });

            if (previewFromS3.fields) {
                setSelectedFileFields(previewFromS3.fields);
            }

            if (previewFromS3.records && previewFromS3.records.length > 0) {
                setSelectedFileData(Object.values(previewFromS3.records.map((record) => Object.values(record))));
            }

            setIsNewFileUploaded(true); // Mark as new file selected

            // In edit mode, clear existing mappings when a new S3 file is selected
            if (mode === 'edit') {
                dispatch(updateAllMappedSelectedFileFields([]));
            }
        }
    }, [sourceState.previewFromS3, dataChannel, previewFromS3, mode, dispatch]);

    const handleChangeDataType = (newDataType) => {
        setDataType(newDataType);
        if (newDataType.value === dataType.value) {
            return;
        }
        if (newDataType.value === 'csv') {
            setDataChannel('upload');
            setSelectedFile(null);
            setSelectedFileFields([]);
            setSelectedFileData([]);
            dispatch(clearS3Files());
        } else {
            let s3BaseKey = '';
            if (newDataType.value === 'avro') {
                s3BaseKey = `${config.sourceInput.s3BaseAvroBucket}${config.sourceInput.s3BaseAvroPath}`;
                setS3BaseKey(s3BaseKey);
            } else if (newDataType.value === 'parquet') {
                s3BaseKey = `${config.sourceInput.s3BaseParquetBucket}${config.sourceInput.s3BaseParquetPath}`;
                setS3BaseKey(s3BaseKey);
            }
            setDataChannel('s3');
            setSelectedFile(null);
            setSelectedFileFields([]);
            setSelectedFileData([]);
            dispatch(clearUploadFiles());
            dispatch(browseS3ForSourceData({ dataType: newDataType.value, s3BaseKey: s3BaseKey }));
        }
    }

    const handleOnUploadFileDrop = (results, file) => {
        // results is a ParseResult object with { data, errors, meta }
        // file is the File object
        if (file && file.type !== 'text/csv' && !file.name.toLowerCase().endsWith('.csv')) {
            setDataPickerOrPreviewAlertMessage('File type is not supported. Only CSV files are currently supported.');
            return;
        }

        if (file && file.size > (32 * 1000000)) {
            setDataPickerOrPreviewAlertMessage('Whoa. Thats a big file. This is just sample data for setting up the mapping. Let\'s keep it under 32MB.');
            return;
        }

        if (file) {
            setSelectedFile(file);
        }

        if (results && results.data && results.data.length > 0) {
            setSelectedFileFields(results.data[0]);
            setSelectedFileData(results.data.slice(1));
            setIsNewFileUploaded(true); // Mark as new file uploaded

            // In edit mode, clear existing mappings when a new file is uploaded
            if (mode === 'edit') {
                dispatch(updateAllMappedSelectedFileFields([]));
            }
        }
    }

    const handleOnUploadFileError = (file, event) => {
        console.error('File upload error:', file, event);
        setDataPickerOrPreviewAlertMessage(`There was an error uploading the file. Please try again.`);
        setSelectedFile(null);
    }

    const handleOnUploadRemoveFile = (data) => {
        // In edit mode, restore original preview data from API
        if (mode === 'edit' && (sourcePreview || sourceData)) {
            console.log('Restoring original file data in edit mode');

            // Restore file name - prefer actual fileName from sourceData over S3 key
            if (sourceData && sourceData.fileName) {
                setSelectedFile({ name: sourceData.fileName });
            } else if (sourcePreview && sourcePreview.fileName) {
                setSelectedFile({ name: sourcePreview.fileName });
            } else {
                setSelectedFile(null);
            }

            // Restore column headers from sourcePreview only
            if (sourcePreview && sourcePreview.fields && sourcePreview.fields.length > 0) {
                setSelectedFileFields(sourcePreview.fields);
            } else {
                setSelectedFileFields([]);
            }

            // Restore preview data rows
            if (sourcePreview && sourcePreview.records && sourcePreview.records.length > 0) {
                const dataRows = sourcePreview.records.map(record => Object.values(record));
                setSelectedFileData(dataRows);
            } else {
                setSelectedFileData([]);
            }

            // Restore original mappings from sourceData
            if (sourceData && sourceData.inputMapping && sourceData.inputMapping.attributes) {
                const existingMappings = Object.entries(sourceData.inputMapping.attributes).map(([property, selectedFileField]) => {
                    return {
                        property,
                        selectedFileField,
                        required: false
                    };
                });
                dispatch(updateAllMappedSelectedFileFields(existingMappings));
            }

            setIsNewFileUploaded(false); // Clear flag when reverting to original
        } else {
            // In create mode, just clear everything
            setSelectedFile(null);
            setSelectedFileFields([]);
            setSelectedFileData([]);
            dispatch(updateAllMappedSelectedFileFields([])); // Clear all mappings
            setIsNewFileUploaded(false); // Clear flag
        }
    }

    const handleReset = () => {
        // Reset button behaves like the X (remove file) - clears file and mappings
        // Works for all data types (CSV, Avro, Parquet) and channels (upload, S3)
        handleOnUploadRemoveFile(null);
        // Also clear any file browser state
        dispatch(clearS3Files({ clearMappings: false })); // mappings already cleared by handleOnUploadRemoveFile
        dispatch(clearUploadFiles({ clearMappings: false }));
        // Reset data type and channel back to defaults (CSV and upload)
        setDataType(dataTypes[0]); // Reset to CSV
        setDataChannel(dataChannels[0].value); // Reset to upload
        setS3BaseKey(''); // Clear S3 base key
    }

    const onFileTreeBrowserFileSelected = (fileKey, dataTypeValue) => {
        dispatch(previewDataOnS3ForSourceData({ s3BaseKey, prefix: fileKey.id, dataType: dataTypeValue }));
    }

    const handleOnMappingSelect = (property, selectedFileField, required) => {
        dispatch(updateMappedSelectedFileFields({ property: property, selectedFileField: selectedFileField, required: required }));
    }

    const handleMapByName = () => {
        if (!selectedInputSchema) console.warn('there are no mapping attributes to auto fill with');
        if (!selectedFileFields) console.warn('there are no file headers map to');
        const mapped = intersectionWith(selectedInputSchema.properties, selectedFileFields, (arrVal, othVal) => {
            if (arrVal.property === othVal) return true;
        });
        const mappedSelectedFileFields = mapped.map((m) => ({ property: m.property, selectedFileField: m.property, required: m.required }));
        dispatch(updateAllMappedSelectedFileFields(mappedSelectedFileFields));
    }

    return (
        <Container>
            <BreadCrumb items={breadcrumbItems} />
            <PanelHeader
                header={mode === 'edit' ? 'Edit Source' : 'Create Source'}
                subHeader={mode === 'edit' ? 'Edit (change) the source' : 'Create a new source'}
            />
            <Panel>
                {loading && <SpinnerOverlay />}
                <Alert
                    variant={"danger"}
                    show={!isEmpty(localAlertMessage)}
                    onClose={() => setLocalAlertMessage('')}
                    dismissible
                    transition={false}
                >
                    {localAlertMessage}
                </Alert>
                <Alert
                    variant={"success"}
                    show={!isEmpty(localSuccessMessage)}
                    onClose={() => setLocalSuccessMessage('')}
                    dismissible
                    transition={false}
                >
                    {localSuccessMessage}
                </Alert>
                <FormContainer>
                    <Form noValidate>
                        <Form.Group controlId={mode === 'edit' ? "editSource" : "createSource"}>
                            <Row>
                                <Col xl={8} lg={8} md={8} sm={8}>
                                    <StyledRow>
                                        <Col xl={5} lg={6} md={6} sm={6}>
                                            <StyledRow>
                                                <Col xl={12} lg={12} md={12} sm={12}>
                                                    <FormSelect
                                                        label={'Type'}
                                                        options={values(TYPES).map((t) => ({
                                                            value: t.id,
                                                            inputType: t.inputType,
                                                            label: t.display + ' (' + t.inputType + ')'
                                                        }))}
                                                        onChange={(v) => setType(v)}
                                                        isInvalid={!formValid && !type}
                                                        value={{ value: type.value, label: type.label }}
                                                        required
                                                        size={'sm'}
                                                    />
                                                </Col>
                                            </StyledRow>
                                        </Col>
                                        <Col xl={5} lg={6} md={6} sm={6}>
                                            <StyledRow>
                                                <Col xl={12} lg={12} md={12} sm={12}>
                                                    <FormNumericInput
                                                        label={'Quality Score'}
                                                        value={qualityScore}
                                                        onChange={value => {
                                                            setQualityScore(value);
                                                            qualityScoreValidate(value);
                                                        }}
                                                        isInvalid={(!formValid && !qualityScore) || qualityScoreInValid}
                                                        errorMsg={qualityScoreInValid ? 'Quality Score must be between 0 and 1' : undefined}
                                                        required
                                                        size={'sm'}
                                                        min={0}
                                                        max={1}
                                                    />
                                                </Col>
                                            </StyledRow>
                                        </Col>
                                    </StyledRow>
                                    <StyledRow>
                                        <Col xl={11} lg={12} md={12} sm={12}>
                                            <FormTextInput
                                                label={'Name'}
                                                value={name}
                                                onChange={value => {
                                                    setName(value);
                                                    nameValidate(value);
                                                }}
                                                isInvalid={(!formValid && !name) || nameInValid}
                                                size={'sm'}
                                                required
                                                errorMsg={nameInValid ? 'Name must match regex ^[A-Za-z0-9_() -]+$' : undefined}
                                            />
                                        </Col>
                                    </StyledRow>
                                    <StyledRow>
                                        <Col xl={11} lg={12} md={12} sm={12}>
                                            <FormTextInput
                                                label={'Label'}
                                                value={label}
                                                onChange={value => {
                                                    setLabel(value);
                                                    labelValidate(value);
                                                }}
                                                isInvalid={(!formValid && !label) || labelInValid}
                                                size={'sm'}
                                                required
                                                errorMsg={labelInValid ? 'Label must match regex ^[A-Za-z0-9_() -]+$' : undefined}
                                            />
                                        </Col>
                                    </StyledRow>
                                    <StyledRow>
                                        <Col xl={11} lg={12} md={12} sm={12}>
                                            <FormTextInput
                                                label={'URL'}
                                                value={url}
                                                onChange={value => {
                                                    setUrl(value);
                                                    urlValidate(value);
                                                }}
                                                isInvalid={(!formValid && !url) || urlInValid}
                                                size={'sm'}
                                                required
                                                errorMsg={urlInValid ? (!url ? 'URL is required' : 'URL must be NA or start with https/http') : undefined}
                                            />
                                        </Col>
                                    </StyledRow>
                                    <Row>
                                        <Col xl={11} lg={12} md={12} sm={12}>
                                            <FormTextArea
                                                label={'Description'}
                                                onChange={value => setDescription(value)}
                                                value={description}
                                                size={'sm'}
                                            />
                                        </Col>
                                    </Row>
                                    <StyledRow>
                                        <Col xl={11} lg={12} md={12} sm={12}>
                                            <FormLabel>Attributes</FormLabel>
                                        </Col>
                                    </StyledRow>
                                    <StyledRow>
                                        <Col xl={3} lg={3} md={3} sm={3}>
                                            <StyledRow>
                                                <Col xl={12} lg={12} md={12} sm={12}>
                                                    <CheckBox
                                                        id="public-source"
                                                        label={'Public Source'}
                                                        checked={isPublic}
                                                        onChange={value => setIsPublic(value)}
                                                    />
                                                </Col>
                                            </StyledRow>
                                            <StyledRow>
                                                <Col xl={11} lg={12} md={12} sm={12}>
                                                    <CheckBox
                                                        id="authoritative-source"
                                                        label={'Authoritative Source'}
                                                        checked={authoritative}
                                                        onChange={value => setAuthoritative(value)}
                                                    />
                                                </Col>
                                            </StyledRow>
                                            <StyledRow>
                                                <Col xl={11} lg={12} md={12} sm={12}>
                                                    <CheckBox
                                                        id="test-source"
                                                        label={'Test Source'}
                                                        checked={isTestSource}
                                                        onChange={value => setIsTestSource(value)}
                                                    />
                                                </Col>
                                            </StyledRow>
                                        </Col>
                                    </StyledRow>
                                    <Row>
                                        <Col xl={11} lg={12} md={12} sm={12}>
                                            <FormSelect
                                                label={'Products'}
                                                options={productOptions}
                                                onChange={(v) => setProducts(v)}
                                                value={products}
                                                isMulti
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xl={11} lg={12} md={12} sm={12}>
                                            <FormSelect
                                                label={'Indicators'}
                                                options={filter(values(INDICATOR_TYPES), (i) => {
                                                    return !!i.display
                                                }).map((t) => ({ value: t.id, label: t.display })
                                                )}
                                                onChange={value => setIndicators(value)}
                                                value={indicators}
                                                isMulti
                                            />
                                        </Col>
                                    </Row>
                                    {/* Geography */}
                                    <div>
                                        <StyledRow>
                                            <Col xl={11} lg={12} md={12} sm={12}>
                                                <SectionTogglePanel
                                                    title={'Geography'}
                                                    isExpand={true}
                                                    overflowX={false}
                                                    padding={'0.5em 0.5em 0.5em 0em'}
                                                >
                                                    <Row>
                                                        <Col xl={12} lg={12} md={12} sm={12}>
                                                            <FormSelect
                                                                label={'Region'}
                                                                options={values(STATES).map((t) => ({
                                                                    value: t.id,
                                                                    label: t.display
                                                                }))}
                                                                onChange={(v) => setRegion(v || '')}
                                                                value={region && region.value ? { value: region.value, label: region.label } : null}
                                                                size={'sm'}
                                                                isClearable={true}
                                                            />
                                                        </Col>
                                                    </Row>
                                                    <StyledRow>
                                                        <Col xl={12} lg={12} md={12} sm={12}>
                                                            <FormTextInput
                                                                label={'City'}
                                                                value={city}
                                                                onChange={value => setCity(value)}
                                                                size={'sm'}
                                                            />
                                                        </Col>
                                                    </StyledRow>
                                                </SectionTogglePanel>
                                            </Col>
                                        </StyledRow>
                                    </div>
                                    {/* Vendors */}
                                    <div>
                                        <StyledRow>
                                            <Col xl={11} lg={12} md={12} sm={12}>
                                                <SectionTogglePanel
                                                    title={'Vendors'}
                                                    isExpand={true}
                                                    overflowX={false}
                                                    padding={'0.5em 0.5em 0.5em 0em'}
                                                >
                                                    <Row>
                                                        <Col xl={12} lg={12} md={12} sm={12}>
                                                            <FormSelect
                                                                label={'Vendor Name'}
                                                                options={vendorOptions}
                                                                onChange={handleVendorNameChange}
                                                                value={vendorName}
                                                                size={'sm'}
                                                            />
                                                        </Col>
                                                    </Row>
                                                    <StyledRow>
                                                        <Col xl={6} lg={6} md={6} sm={6}>
                                                            <FormTextInput
                                                                label={'Vendor Reference Key 1'}
                                                                value={vendorRefs.ref1.key}
                                                                onChange={(value) => setVendorRefs(prev => ({
                                                                    ...prev,
                                                                    ref1: { ...prev.ref1, key: value }
                                                                }))}
                                                                size={'sm'}
                                                                isInvalid={!formValid && vendorRefs.ref1.value && !vendorRefs.ref1.key}
                                                                disabled={vendorName?.value === 'FGX'}
                                                            />
                                                        </Col>
                                                        <Col xl={6} lg={6} md={6} sm={6}>
                                                            <FormTextInput
                                                                label={'Vendor Reference Value 1'}
                                                                value={vendorRefs.ref1.value}
                                                                onChange={(value) => setVendorRefs(prev => ({
                                                                    ...prev,
                                                                    ref1: { ...prev.ref1, value: value }
                                                                }))}
                                                                size={'sm'}
                                                            />
                                                        </Col>
                                                    </StyledRow>
                                                    <StyledRow>
                                                        <Col xl={6} lg={6} md={6} sm={6}>
                                                            <FormTextInput
                                                                label={'Vendor Reference Key 2'}
                                                                value={vendorRefs.ref2.key}
                                                                onChange={(value) => setVendorRefs(prev => ({
                                                                    ...prev,
                                                                    ref2: { ...prev.ref2, key: value }
                                                                }))}
                                                                size={'sm'}
                                                                isInvalid={!formValid && vendorRefs.ref2.value && !vendorRefs.ref2.key}
                                                                disabled={vendorName?.value === 'FGX'}
                                                            />
                                                        </Col>
                                                        <Col xl={6} lg={6} md={6} sm={6}>
                                                            <FormTextInput
                                                                label={'Vendor Reference Value 2'}
                                                                value={vendorRefs.ref2.value}
                                                                onChange={(value) => setVendorRefs(prev => ({
                                                                    ...prev,
                                                                    ref2: { ...prev.ref2, value: value }
                                                                }))}
                                                                size={'sm'}
                                                            />
                                                        </Col>
                                                    </StyledRow>
                                                    <StyledRow>
                                                        <Col xl={6} lg={6} md={6} sm={6}>
                                                            <FormTextInput
                                                                label={'Vendor Reference Key 3'}
                                                                value={vendorRefs.ref3.key}
                                                                onChange={(value) => setVendorRefs(prev => ({
                                                                    ...prev,
                                                                    ref3: { ...prev.ref3, key: value }
                                                                }))}
                                                                size={'sm'}
                                                                isInvalid={!formValid && vendorRefs.ref3.value && !vendorRefs.ref3.key}
                                                                disabled={vendorName?.value === 'FGX'}
                                                            />
                                                        </Col>
                                                        <Col xl={6} lg={6} md={6} sm={6}>
                                                            <FormTextInput
                                                                label={'Vendor Reference Value 3'}
                                                                value={vendorRefs.ref3.value}
                                                                onChange={(value) => setVendorRefs(prev => ({
                                                                    ...prev,
                                                                    ref3: { ...prev.ref3, value: value }
                                                                }))}
                                                                size={'sm'}
                                                            />
                                                        </Col>
                                                    </StyledRow>
                                                    <StyledRow>
                                                        <Col xl={6} lg={6} md={6} sm={6}>
                                                            <FormTextInput
                                                                label={'Vendor Reference Key 4'}
                                                                value={vendorRefs.ref4.key}
                                                                onChange={(value) => {
                                                                    setVendorRefs(prev => ({
                                                                        ...prev,
                                                                        ref4: { ...prev.ref4, key: value }
                                                                    }));
                                                                }}
                                                                size={'sm'}
                                                                isInvalid={!formValid && vendorRefs.ref4.value && !vendorRefs.ref4.key}
                                                            />
                                                        </Col>
                                                        <Col xl={6} lg={6} md={6} sm={6}>
                                                            <FormTextInput
                                                                label={'Vendor Reference Value 4'}
                                                                value={vendorRefs.ref4.value}
                                                                onChange={(value) => {
                                                                    setVendorRefs(prev => ({
                                                                        ...prev,
                                                                        ref4: { ...prev.ref4, value: value }
                                                                    }));
                                                                }}
                                                                size={'sm'}
                                                            />
                                                        </Col>
                                                    </StyledRow>
                                                    <StyledRow>
                                                        <Col xl={6} lg={6} md={6} sm={6}>
                                                            <FormTextInput
                                                                label={'Vendor Reference Key 5'}
                                                                value={vendorRefs.ref5.key}
                                                                onChange={(value) => {
                                                                    setVendorRefs(prev => ({
                                                                        ...prev,
                                                                        ref5: { ...prev.ref5, key: value }
                                                                    }));
                                                                }}
                                                                size={'sm'}
                                                                isInvalid={!formValid && vendorRefs.ref5.value && !vendorRefs.ref5.key}
                                                            />
                                                        </Col>
                                                        <Col xl={6} lg={6} md={6} sm={6}>
                                                            <FormTextInput
                                                                label={'Vendor Reference Value 5'}
                                                                value={vendorRefs.ref5.value}
                                                                onChange={(value) => {
                                                                    setVendorRefs(prev => ({
                                                                        ...prev,
                                                                        ref5: { ...prev.ref5, value: value }
                                                                    }));
                                                                }}
                                                                size={'sm'}
                                                            />
                                                        </Col>
                                                    </StyledRow>
                                                </SectionTogglePanel>
                                            </Col>
                                        </StyledRow>
                                    </div>
                                    {/* Pipeline Config*/}
                                    <div>
                                        <StyledRow>
                                            <Col xl={11} lg={12} md={12} sm={12}>
                                                <SectionTogglePanel
                                                    title={'Pipeline'}
                                                    isExpand={true}
                                                    overflowX={false}
                                                    padding={'0.5em 0.5em 0.5em 0em'}
                                                >
                                                    <StyledRow>
                                                        <Col xl={12} lg={12} md={12} sm={12}>
                                                            <FormTextInput
                                                                label={'Repository'}
                                                                value={pipelineRepository}
                                                                onChange={value => {
                                                                    setPipelineRepository(value)
                                                                }}
                                                            />
                                                        </Col>
                                                    </StyledRow>
                                                    <StyledRow>
                                                        <Col xl={12} lg={12} md={12} sm={12}>
                                                            <FormTextInput
                                                                label={'File Name Prefix'}
                                                                value={pipelineFileNamePrefix}
                                                                onChange={value => {
                                                                    setPipelineFileNamePrefix(value);
                                                                    pipelineFileNamePrefixValidate(value);
                                                                }}
                                                                isInvalid={pipelineFileNamePrefixInValid}
                                                                errorMsg={pipelineFileNamePrefixInValid ? 'file name prefix must end with an underscore (_)' : undefined}
                                                                size={'sm'}
                                                            />
                                                        </Col>
                                                    </StyledRow>
                                                </SectionTogglePanel>
                                            </Col>
                                        </StyledRow>
                                    </div>
                                    {/* Data Config, Preview and Mapping */}
                                    <div>
                                        <StyledRow>
                                            <Col xl={11} lg={12} md={12} sm={12}>
                                                <SectionTogglePanel
                                                    title={'Data Config, Preview and Mapping'}
                                                    isExpand={true}
                                                    overflowX={false}
                                                    padding={'0.5em 0.5em 0.5em 0em'}
                                                >
                                                    <Row>
                                                        <Col xl={12} lg={12} md={12} sm={12}>
                                                            <FormSelect
                                                                label={'Data Type'}
                                                                options={dataTypes}
                                                                onChange={(v) => handleChangeDataType(v)}
                                                                value={dataType}
                                                                size={'sm'}
                                                            />
                                                        </Col>
                                                    </Row>
                                                    <StyledRow style={{ alignItems: 'flex-end' }}>
                                                        <Col xl={9} lg={8} md={8} sm={7}>
                                                            <FormRadioGroup
                                                                id={'DataChannelSelection'}
                                                                name={'DataChannelSelection'}
                                                                label={'Data Channel'}
                                                                value={dataChannel}
                                                                items={dataChannels}
                                                                onChange={selectedValue => console.log('Choice restricted by data type.')}
                                                                radioButton
                                                            />
                                                        </Col>
                                                        <Col xl={3} lg={4} md={4} sm={5}>
                                                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                                <Button
                                                                    className="btn-min-width-120 btn-height-35"
                                                                    style={{ marginBottom: "7px" }}
                                                                    variant="info"
                                                                    onClick={handleReset}
                                                                >RESET
                                                                </Button>
                                                            </div>
                                                        </Col>
                                                    </StyledRow>
                                                    {
                                                        dataChannel === 's3' && (
                                                            <Row>
                                                                <Col xl={12} lg={12} md={12} sm={12}>
                                                                    <FormTextInput
                                                                        label={'Base Key (S3 Bucket + Path)'}
                                                                        value={s3BaseKey}
                                                                        onChange={value => {
                                                                            setS3BaseKey(value)
                                                                        }}
                                                                        disabled
                                                                    />
                                                                </Col>
                                                            </Row>
                                                        )
                                                    }
                                                    <StyledRow>
                                                        <Col xl={12} lg={12} md={12} sm={12}>
                                                            <Alert
                                                                variant={"danger"}
                                                                show={!isEmpty(dataPickerOrPreviewAlertMessage)}
                                                                onClose={() => setDataPickerOrPreviewAlertMessage('')}
                                                                dismissible
                                                                transition={false}
                                                            >
                                                                {dataPickerOrPreviewAlertMessage}
                                                            </Alert>
                                                        </Col>
                                                    </StyledRow>
                                                    {dataChannel === 'upload' && (
                                                        <StyledRow>
                                                            <Col xl={12} lg={12} md={12} sm={12}>
                                                                <CSVReader
                                                                    config={{ preview: 51 }}
                                                                    onUploadAccepted={handleOnUploadFileDrop}
                                                                    onUploadRejected={handleOnUploadFileError}
                                                                    accept=".csv"
                                                                    maxSize={32 * 1000000}
                                                                >
                                                                    {({
                                                                        getRootProps,
                                                                        acceptedFile,
                                                                        ProgressBar,
                                                                        getRemoveFileProps,
                                                                        Remove,
                                                                    }) => (
                                                                        <>
                                                                            <div
                                                                                {...getRootProps()}
                                                                                style={{
                                                                                    border: 'dashed',
                                                                                    borderColor: '#ccc',
                                                                                    borderRadius: '1px',
                                                                                    borderWidth: '1px',
                                                                                    marginBottom: 10,
                                                                                    height: acceptedFile ? '200px' : '400px',
                                                                                    cursor: 'pointer',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    padding: '20px',
                                                                                }}
                                                                            >
                                                                                <SourceDrop>
                                                                                    <Image
                                                                                        src={BrowserWindowImage}
                                                                                        width="110"
                                                                                        alt="Browser Window Image"
                                                                                        fluid
                                                                                    />
                                                                                    <FormLabel>
                                                                                        {mode === 'edit' && sourcePreview
                                                                                            ? "Drag n' drop or click to change your sample file"
                                                                                            : "Drag n' drop your file here or click to select your file"
                                                                                        }
                                                                                    </FormLabel>
                                                                                </SourceDrop>
                                                                            </div>
                                                                            {acceptedFile && (
                                                                                <div style={{ marginBottom: '10px' }}>
                                                                                    <div style={{
                                                                                        color: '#2d5d7e',
                                                                                        backgroundColor: '#ffffff',
                                                                                        borderRadius: '1px',
                                                                                        borderWidth: '1px',
                                                                                        fontSize: 14,
                                                                                        lineHeight: 1,
                                                                                        padding: '0 0.4em',
                                                                                        marginBottom: '0.5em',
                                                                                    }}>
                                                                                        {acceptedFile.name}
                                                                                    </div>
                                                                                    <div style={{
                                                                                        color: '#6c757d',
                                                                                        backgroundColor: '#ffffff',
                                                                                        borderRadius: '1px',
                                                                                        borderWidth: '1px',
                                                                                        lineHeight: 1,
                                                                                        marginBottom: '0.5em',
                                                                                        padding: '0 0.4em',
                                                                                    }}>
                                                                                        {(acceptedFile.size / 1024).toFixed(2)} KB
                                                                                    </div>
                                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                                        <ProgressBar style={{ backgroundColor: '#EE5A38' }} />
                                                                                        <button
                                                                                            {...getRemoveFileProps()}
                                                                                            onClick={(e) => {
                                                                                                getRemoveFileProps().onClick(e);
                                                                                                handleOnUploadRemoveFile(null);
                                                                                            }}
                                                                                            style={{
                                                                                                color: '#EE5A38',
                                                                                                background: 'none',
                                                                                                border: 'none',
                                                                                                cursor: 'pointer',
                                                                                                padding: '5px',
                                                                                            }}
                                                                                        >
                                                                                            <Remove />
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </CSVReader>
                                                            </Col>
                                                        </StyledRow>
                                                    )}
                                                    {dataChannel === 's3' && (
                                                        <StyledRow>
                                                            <Col xl={12} lg={12} md={12} sm={12}>
                                                                <StyledFileTreeBrowser style={{ height: 400, marginBottom: '10px' }}>
                                                                    <FileTreeBrowser
                                                                        baseKey={s3BaseKey}
                                                                        files={s3Files}
                                                                        onFileAction={(fileKey) => onFileTreeBrowserFileSelected(fileKey, dataType.value)}
                                                                    />
                                                                </StyledFileTreeBrowser>
                                                            </Col>
                                                        </StyledRow>
                                                    )}
                                                    {/* Preview */}
                                                    <StyledRow>
                                                        <Col xl={12} lg={12} md={12} sm={12}>
                                                            <DataPreview
                                                                fields={selectedFileFields}
                                                                data={selectedFileData}
                                                                title={`Preview - ${selectedFile?.name}`}
                                                                emptyStateMessage="Please configure your source to preview the data"
                                                                showRowCount={true}
                                                                cellMaxWidth="500px"
                                                                truncateLength={75}
                                                            />
                                                        </Col>
                                                    </StyledRow>
                                                    {/* Mapping */}
                                                    <StyledRow>
                                                        <Col xl={12} lg={12} md={12} sm={12}>
                                                            <FormLabel>Mapping</FormLabel>
                                                            <MappingContainer>
                                                                {(!type || !selectedFile) && (
                                                                    <EmptyStateContainer>
                                                                        <FormLabel>
                                                                            Please configure your source to preview the data
                                                                        </FormLabel>
                                                                    </EmptyStateContainer>
                                                                )}
                                                                {type && selectedFile && selectedFileFields && (
                                                                    <>
                                                                        <MappingHeader>
                                                                            <div>{`Mappable Properties for Source: ${type.label}`}</div>
                                                                            <div>Selected Fields from Configured Source</div>
                                                                        </MappingHeader>
                                                                        {mappableInputSchemaProperties && mappableInputSchemaProperties.map((mp, idx) => {
                                                                            const selectedFileField = find(mappedSelectedFileFields, (o) => {
                                                                                return o.property === mp.property
                                                                            }) && find(mappedSelectedFileFields, (o) => {
                                                                                return o.property === mp.property
                                                                            }).selectedFileField;
                                                                            const alreadyUsedSelectedFileField = mappedSelectedFileFields && mappedSelectedFileFields.map((o) => { return o.selectedFileField });
                                                                            const selectedFileFieldsToDisplay = selectedFileField ? concat(
                                                                                selectedFileField,
                                                                                difference(selectedFileFields, alreadyUsedSelectedFileField)
                                                                            ) : difference(selectedFileFields, alreadyUsedSelectedFileField);
                                                                            const options = selectedFileFieldsToDisplay && selectedFileFieldsToDisplay.map(field => { return { value: field, label: field } });
                                                                            return (
                                                                                <MappingRow key={`mappingRecord-${idx}`}>
                                                                                    <FormControl
                                                                                        value={mp.display}
                                                                                        disabled
                                                                                        type={"text"}
                                                                                        size="sm"
                                                                                    />
                                                                                    <FormSelect
                                                                                        options={options}
                                                                                        onChange={(v) => handleOnMappingSelect(mp.property, v ? v.value : null, mp.required)}
                                                                                        value={{ value: selectedFileField, label: selectedFileField }}
                                                                                        isClearable={!!selectedFileField}
                                                                                        placeholder={'Select a field'}
                                                                                        size="sm"
                                                                                    />
                                                                                </MappingRow>
                                                                            )
                                                                        })}
                                                                        <StyledRow>
                                                                            <Col xl={12} lg={12} md={12} sm={12} className="buttons">
                                                                                <Button
                                                                                    className="btn-min-width-120 btn-height-35"
                                                                                    variant="outline-secondary"
                                                                                    onClick={handleMapByName}
                                                                                >MAP BY NAME
                                                                                </Button>
                                                                            </Col>
                                                                        </StyledRow>
                                                                    </>
                                                                )}
                                                            </MappingContainer>
                                                        </Col>
                                                    </StyledRow>
                                                </SectionTogglePanel>
                                            </Col>
                                        </StyledRow>
                                    </div>
                                </Col>
                                <Col xl={4} lg={4} md={4} sm={4}>
                                    <HelpPanel
                                        title=""
                                        height={type && selectedFile ? "500vh" : "200vh"}
                                        data={[
                                            { property: 'type', description: 'dictates input processing rules. Options are Source Type (Input Type)' },
                                            { property: 'qualityScore', description: <>qualityScore: <br />- defaults is 0.8<br />- 0.9 is great<br />- 0.7 is below average<br />we should not have any sources that are below 0.7 in active use</> },
                                            { property: 'name', description: 'internal not exposed to users, should be descriptive of source, required' },
                                            { property: 'label', description: 'external, will be exposed to end users, required' },
                                            { property: 'url', description: 'should document where data is collected from for a given source. if no URL set to NA (this is very unlikely)' },
                                            {
                                                property: 'attributes', description: <>
                                                    - public: source is publicly accessible<br />
                                                    - authoritative: source is deemed authoritatitve (reputable)<br />
                                                    - test: inputs associated to test source are not processed
                                                </>
                                            },
                                            { property: 'pipelineRepository', description: 'the URL of the project that processes this source' },
                                            { property: 'pipelineFileNamePrefix', description: 'leveraged by the input pipeline to identify the data to process. must end with an underscore (_)' },
                                            { property: '[[Data Config, Preview and Mapping]]', description: <>We store a preview dataset with the source, this data is mapped for use in input processing. This isn't required but it is expected. <b>Unexpected behaviour may result if a sample dataset and mapping is not provided</b>.</> },
                                            { property: 'dataChannel', description: 'dataChannel: where the data is coming from; currently upload only supports CSV (dataType) and s3 only supports AVRO and Parquet' },
                                        ]}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col xl={12} lg={12} md={12} sm={12} className="buttons">
                                    <Button
                                        className="btn-min-width-120 btn-height-35"
                                        variant="info"
                                        onClick={handleSubmit}
                                    >{mode === 'edit' ? 'EDIT' : 'CREATE'}
                                    </Button>
                                    <Button
                                        className="btn-min-width-120 btn-height-35"
                                        style={{ marginLeft: "10px" }}
                                        variant="outline-secondary"
                                        onClick={onCancel}
                                    >CANCEL
                                    </Button>
                                </Col>
                            </Row>
                        </Form.Group>
                    </Form>
                </FormContainer>
            </Panel>
        </Container>
    );
}

SourceForm.propTypes = {
    mode: PropTypes.oneOf(['create', 'edit']).isRequired,
    initialData: PropTypes.object,
    sourceData: PropTypes.object,
    sourcePreview: PropTypes.object,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    breadcrumbItems: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    alertMessage: PropTypes.object,
    successMessage: PropTypes.object,
};

SourceForm.defaultProps = {
    initialData: null,
    sourceData: null,
    sourcePreview: null,
    loading: false,
    alertMessage: {},
    successMessage: {},
};

export default SourceForm;

